/* ============================================================
   Skin Moods — Editorial Design System behaviour
   ------------------------------------------------------------
   Two reusable patterns, one interaction language:

   1. [data-sm-ed-index]  interactive editorial list
      hover previews · click locks · tap selects · arrows move
      Drives the cortisol findings, the formulation pathways,
      the three-part module and the scent list. Indexes nest.

   2. [data-sm-ed-five]   five-collection index
      hover emphasises one collection and its colour segment.

   2b. [data-sm-ed-pick]  editorial picker
      the same gesture over a list of links: hover brings one
      forward, click follows it. No panels, no roving tabindex.

   3. [data-sm-ed-reveal] one-shot entrance on first view.

   4. [data-sm-ed-snap]   the page reads as a deck of chapters.

   5. [data-sm-ed-lock]   a chapter holds while its content advances.

   6. [data-sm-hero]      a muted background film with a real
      play/pause control. Autoplay is started here, never as an
      attribute, so a motion preference is read before a frame runs.

   Everything respects prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* Collect descendants that belong to THIS root and not to a nested one. */
  function own(root, selector, ownerSelector) {
    var all = root.querySelectorAll(selector);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].closest(ownerSelector) === root) out.push(all[i]);
    }
    return out;
  }

  /* ----------------------------------------------------------
     0. Header height, published as --sm-ed-header-h
     Cover sections size themselves against it.
     ---------------------------------------------------------- */
  function measureHeader() {
    var header = document.querySelector('.shopify-section-group-header-group')
      || document.getElementById('HeaderWrapper');
    if (!header) return;
    /* Only ever measured at rest. Scrolled, the header shrinks and can
       detach entirely, and the number wanted here is the one thing it
       is used for: how much of the first screen the header takes when
       the reader lands on it. Sampling a scrolled header shortened
       every cover section on the page by the difference. */
    if (window.pageYOffset > 4) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--sm-ed-header-h', h + 'px');
  }

  /* ----------------------------------------------------------
     1. Interactive editorial index

     Hover behaviour is per-index, set with data-sm-ed-index-hover:
       "preview" (default) hover previews, leaving the whole index
                 returns to the locked choice. Used where the content
                 sits opposite a list of alternatives.
       "select"  hover commits. Nothing reverts, so the reader can
                 move onto the content and read it.
       "none"    click and keyboard only. Used for top-level tabs,
                 which must never change under a passing cursor.
     ---------------------------------------------------------- */
  function initIndex(root) {
    if (root.getAttribute('data-sm-ed-ready') === 'true') return;
    root.setAttribute('data-sm-ed-ready', 'true');

    var sel = '[data-sm-ed-index]';
    var mode = root.getAttribute('data-sm-ed-index-hover') || 'preview';
    var items = own(root, '[data-sm-ed-index-item]', sel);
    var panels = own(root, '[data-sm-ed-index-panel]', sel);
    var segs = own(root, '[data-sm-ed-index-seg]', sel);
    if (!items.length) return;

    var locked = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('is-active')) { locked = i; break; }
    }

    function show(n) {
      /* One accent per index, published on the root so the meter, the
         active row and the drawing opposite it all read the same value.
         An index whose items name no accent stays monochrome — the
         property is removed rather than set to ink, so every
         var(--sm-ed-accent, …) falls back to its own neutral. */
      var accent = items[n] && items[n].getAttribute('data-sm-ed-accent');
      if (accent) root.style.setProperty('--sm-ed-accent', accent);
      else root.style.removeProperty('--sm-ed-accent');

      for (var i = 0; i < items.length; i++) {
        var on = i === n;
        items[i].classList.toggle('is-active', on);
        /* Tabs announce selection; a self-contained row announces expansion. */
        if (items[i].getAttribute('role') === 'tab') {
          items[i].setAttribute('aria-selected', on ? 'true' : 'false');
        } else {
          items[i].setAttribute('aria-expanded', on ? 'true' : 'false');
        }
        items[i].setAttribute('tabindex', i === locked ? '0' : '-1');
      }
      for (i = 0; i < segs.length; i++) {
        segs[i].classList.toggle('is-on', i === n);
      }
      for (i = 0; i < panels.length; i++) {
        var panel = panels[i];
        if (i === n) {
          panel.hidden = false;
          if (reduceMotion) {
            panel.classList.add('is-in');
          } else {
            panel.classList.remove('is-in');
            void panel.offsetHeight; /* reflow so the transition plays */
            /* `is-in` is not only the entrance, it is the visible state —
               and everything the panel draws on entering hangs off it.
               A tab opened in the background runs no animation frames, so
               on rAF alone the panel would sit at opacity 0 until it was
               looked at. The timer cannot beat a frame in a visible tab,
               so the entrance still plays; it only guarantees the panel
               is never left waiting for a frame that is not coming. */
            (function (el) {
              var shown = false;
              function enter() {
                if (shown) return;
                shown = true;
                el.classList.add('is-in');
              }
              requestAnimationFrame(enter);
              window.setTimeout(enter, 120);
            })(panel);
          }
        } else {
          panel.hidden = true;
          panel.classList.remove('is-in');
        }
      }
      root.dispatchEvent(new CustomEvent('sm-ed:change', { detail: { index: n } }));
    }

    function select(n, moveFocus) {
      locked = n;
      show(n);
      if (moveFocus && items[n]) items[n].focus();
    }

    for (var k = 0; k < items.length; k++) {
      (function (idx) {
        var item = items[idx];

        item.addEventListener('click', function () { select(idx, false); });

        item.addEventListener('keydown', function (event) {
          var key = event.key;
          var next = -1;
          if (key === 'ArrowDown' || key === 'ArrowRight') next = (idx + 1) % items.length;
          else if (key === 'ArrowUp' || key === 'ArrowLeft') next = (idx - 1 + items.length) % items.length;
          else if (key === 'Home') next = 0;
          else if (key === 'End') next = items.length - 1;
          if (next < 0) return;
          event.preventDefault();
          select(next, true);
        });

        if (canHover && mode !== 'none') {
          item.addEventListener('mouseenter', function () {
            if (mode === 'select') select(idx, false); else show(idx);
          });
        }
      })(k);
    }

    for (var s = 0; s < segs.length; s++) {
      (function (idx) {
        segs[idx].addEventListener('click', function () { select(idx, false); });
        if (canHover && mode !== 'none') {
          segs[idx].addEventListener('mouseenter', function () {
            if (mode === 'select') select(idx, false); else show(idx);
          });
        }
      })(s);
    }

    /* Only a preview reverts, and only once the pointer has left the whole
       index — moving from the list across to the content it controls must
       not snap the content back. */
    if (canHover && mode === 'preview') {
      root.addEventListener('mouseleave', function () { show(locked); });
    }

    /* Hold the stage at the active panel's height so switching tabs eases
       the page below instead of snapping it. */
    var stage = own(root, '[data-sm-ed-index-stage]', sel)[0];
    if (stage) {
      var sizeStage = function () {
        var open = null;
        for (var i = 0; i < panels.length; i++) {
          if (!panels[i].hidden) { open = panels[i]; break; }
        }
        if (!open) return;
        var pad = parseFloat(getComputedStyle(stage).paddingTop) || 0;
        stage.style.minHeight = (open.getBoundingClientRect().height + pad) + 'px';
      };
      root.addEventListener('sm-ed:change', sizeStage);
      window.addEventListener('resize', function () {
        /* Drop the floor first so the panel can reflow to its natural
           height at the new width, then measure that. */
        stage.style.minHeight = '';
        window.requestAnimationFrame(sizeStage);
      });
      window.addEventListener('load', sizeStage);
      requestAnimationFrame(sizeStage);
    }

    show(locked);
  }

  /* ----------------------------------------------------------
     2. Five-collection index
     ---------------------------------------------------------- */
  function initFive(root) {
    if (root.getAttribute('data-sm-ed-ready') === 'true') return;
    root.setAttribute('data-sm-ed-ready', 'true');

    var items = root.querySelectorAll('[data-sm-ed-five-item]');
    var segs = root.querySelectorAll('[data-sm-ed-five-seg]');
    if (!items.length) return;

    /* Emphasis is transient: it belongs to the pointer, so the resting
       page shows all five collections at equal weight. Each collection
       now carries its own object, so there is no shared stage to keep
       in step with it. */
    function emphasise(n) {
      if (n === null) {
        root.removeAttribute('data-sm-ed-active');
      } else {
        root.setAttribute('data-sm-ed-active', String(n));
      }
      for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-on', i === n);
      for (i = 0; i < segs.length; i++) segs[i].classList.toggle('is-on', i === n);
    }

    for (var k = 0; k < items.length; k++) {
      (function (idx) {
        var item = items[idx];
        if (canHover) {
          item.addEventListener('mouseenter', function () { emphasise(idx); });
          item.addEventListener('mouseleave', function () { emphasise(null); });
        }
        item.addEventListener('focus', function () { emphasise(idx); });
        item.addEventListener('blur', function () { emphasise(null); });
      })(k);
    }
  }

  /* ----------------------------------------------------------
     2b. Editorial picker

     The index in section 1 owns a stage: it hides and shows panels,
     manages a roving tabindex and announces selection, because there
     the row is a control and the panel is the content it controls.

     A picker is the same gesture over a list whose rows are already
     links — the Landing page's five collections. Hovering one brings
     its visual forward; clicking it goes to the collection. So this
     deliberately does none of the index's control work: it never
     touches tabindex, never sets aria-selected, never hides anything.
     Every row stays a link in the tab order that goes where it says.
     All it does is move one class and publish one colour, and the
     stage is CSS.

     One row is always on. Nothing reverts on leaving — the reader
     moves from the name across to the visual it just brought up, and
     snapping back would take it away exactly as they arrived.
     ---------------------------------------------------------- */
  function initPick(root) {
    if (root.getAttribute('data-sm-ed-ready') === 'true') return;
    root.setAttribute('data-sm-ed-ready', 'true');

    var items = own(root, '[data-sm-ed-pick-item]', '[data-sm-ed-pick]');
    var segs = own(root, '[data-sm-ed-pick-seg]', '[data-sm-ed-pick]');
    if (!items.length) return;

    var current = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('is-on')) { current = i; break; }
    }

    function pick(n) {
      if (n === current) return;
      current = n;
      var accent = items[n] && items[n].getAttribute('data-sm-ed-accent');
      if (accent) root.style.setProperty('--sm-ed-accent', accent);
      else root.style.removeProperty('--sm-ed-accent');

      for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-on', i === n);
      for (i = 0; i < segs.length; i++) segs[i].classList.toggle('is-on', i === n);
      root.setAttribute('data-sm-ed-on', String(n));
      root.dispatchEvent(new CustomEvent('sm-ed:pick', { detail: { index: n } }));
    }

    for (var k = 0; k < items.length; k++) {
      (function (idx) {
        if (canHover) items[idx].addEventListener('mouseenter', function () { pick(idx); });
        /* Keyboard reaches the same state by tabbing, which is the only
           way to reach it without a pointer. */
        items[idx].addEventListener('focus', function () { pick(idx); });
      })(k);
    }

    for (var s = 0; s < segs.length; s++) {
      (function (idx) {
        if (canHover) segs[idx].addEventListener('mouseenter', function () { pick(idx); });
      })(s);
    }

    /* Publish the resting row's colour, so the page opens on it. */
    var first = items[current].getAttribute('data-sm-ed-accent');
    if (first) root.style.setProperty('--sm-ed-accent', first);
    root.setAttribute('data-sm-ed-on', String(current));
  }

  /* ----------------------------------------------------------
     2c. Mood tabs  [data-sm-moods]

     The index in section 1 owns a stage it measures: it hides panels
     with the `hidden` attribute and then holds the stage at the open
     panel's height, which is right where the panels are diagrams of
     different sizes inside a paragraph.

     A mood plate is the opposite case. All five panels are the same
     composition with different content, they are stacked in one grid
     cell by CSS, and the stage is already as tall as the tallest of
     them — so there is nothing to measure and nothing to animate to,
     and a measured floor would be the one thing that made the page
     jump. Hiding is `visibility`, set in CSS off `.is-active`, which
     takes an inactive panel out of the accessibility tree and out of
     the tab order in the same declaration.

     So all this does is move one class and the tab state. Click and
     keyboard only: the nav sits above the plate it controls, directly
     in the path of a pointer travelling down the page, and on hover it
     would sweep all five markers and strobe the picture on the way.
     ---------------------------------------------------------- */
  function initMoods(root) {
    if (root.getAttribute('data-sm-ed-ready') === 'true') return;
    root.setAttribute('data-sm-ed-ready', 'true');

    var sel = '[data-sm-moods]';
    var tabs = own(root, '[data-sm-moods-tab]', sel);
    var panels = own(root, '[data-sm-moods-panel]', sel);
    if (tabs.length < 2) return;

    var current = 0;
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].classList.contains('is-active')) { current = i; break; }
    }

    function show(n, moveFocus) {
      current = n;
      for (var i = 0; i < tabs.length; i++) {
        var on = i === n;
        tabs[i].classList.toggle('is-active', on);
        tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        /* One stop in the page's tab order for the whole tablist; the
           arrows move between the five once it is reached. */
        tabs[i].setAttribute('tabindex', on ? '0' : '-1');
      }
      for (i = 0; i < panels.length; i++) {
        panels[i].classList.toggle('is-active', i === n);
      }
      if (moveFocus && tabs[n]) {
        tabs[n].focus();
        /* The nav scrolls sideways on a phone. `nearest` moves it only
           when the chosen name is actually out of view, and never moves
           the page itself when it is not. */
        if (tabs[n].scrollIntoView) {
          tabs[n].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
      root.dispatchEvent(new CustomEvent('sm-moods:change', { detail: { index: n } }));
    }

    for (var k = 0; k < tabs.length; k++) {
      (function (idx) {
        tabs[idx].addEventListener('click', function () {
          if (idx !== current) show(idx, false);
        });

        tabs[idx].addEventListener('keydown', function (event) {
          var key = event.key;
          var next = -1;
          if (key === 'ArrowRight' || key === 'ArrowDown') next = (idx + 1) % tabs.length;
          else if (key === 'ArrowLeft' || key === 'ArrowUp') next = (idx - 1 + tabs.length) % tabs.length;
          else if (key === 'Home') next = 0;
          else if (key === 'End') next = tabs.length - 1;
          if (next < 0) return;
          event.preventDefault();
          show(next, true);
        });
      })(k);
    }

    show(current, false);
  }

  /* ----------------------------------------------------------
     3. Reveal on first view
     ---------------------------------------------------------- */
  /* Turning a reveal on, and telling the page it happened — a section
     that has to follow an entrance with one of its own listens for
     this rather than racing the same observer with a second one. */
  function reveal(el) {
    el.classList.add('is-in');
    el.dispatchEvent(new CustomEvent('sm-ed:reveal', { bubbles: true }));
  }

  /* Every observer this page opened, each able to re-examine what it is
     still holding. The partition below is made once, from the geometry of
     a single instant; the sweep is what lets a later instant correct it. */
  var revealSweeps = [];
  function sweepReveals() {
    for (var i = 0; i < revealSweeps.length; i++) revealSweeps[i]();
  }

  function initReveal(root) {
    var targets = root.querySelectorAll('.sm-ed-reveal');
    if (!targets.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var i = 0; i < targets.length; i++) reveal(targets[i]);
      return;
    }
    var pending = [];
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
        var at = pending.indexOf(entry.target);
        if (at !== -1) pending.splice(at, 1);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    /* The observer's root is deliberately 8% short at the foot, so an
       entrance never fires on a line that is only just peeking in. The
       cost is a blind band: anything that STARTS below the fold and then
       moves up into that band without the page scrolling — the header
       leaving flow under a full-height hero, a web font settling, an
       image finally giving the block its height — intersects nothing and
       waits forever, which on the opening screen means CTAs that never
       arrive. This sweep closes the band once the layout is final. It
       only ever reveals, so it cannot undo an entrance that has played,
       and it leaves everything genuinely below the fold to the observer. */
    revealSweeps.push(function () {
      for (var k = pending.length - 1; k >= 0; k--) {
        var el = pending[k];
        if (el.classList.contains('is-in')) { pending.splice(k, 1); continue; }
        if (el.getBoundingClientRect().top < window.innerHeight) {
          reveal(el);
          observer.unobserve(el);
          pending.splice(k, 1);
        }
      }
    });

    var atLoad = [];
    for (var j = 0; j < targets.length; j++) {
      /* Anything already at or above the fold — including everything the
         browser skipped past when it restored a scroll position — is shown
         straight away. Only what is still below gets an entrance. */
      if (targets[j].getBoundingClientRect().top < window.innerHeight) {
        atLoad.push(targets[j]);
      } else {
        pending.push(targets[j]);
        observer.observe(targets[j]);
      }
    }
    if (!atLoad.length) return;

    /* Two frames, not zero. The resting state and `is-in` are set in the
       same task on a first paint, so the browser resolves one computed
       style, finds no change to interpolate and drops the transition —
       which is why the opening composition arrived already assembled.
       Waiting for the resting state to paint gives it something to
       animate from.

       The timer is not a nicety. A tab loaded in the background runs no
       animation frames at all, so on rAF alone the opening composition
       would sit at opacity 0 until the tab was looked at. A timer fires
       either way, and whichever lands first wins — in a visible tab the
       frames get there long before 400ms, so the entrance still plays. */
    var released = false;
    function release() {
      if (released) return;
      released = true;
      for (var i = 0; i < atLoad.length; i++) reveal(atLoad[i]);
    }
    requestAnimationFrame(function () { requestAnimationFrame(release); });
    window.setTimeout(release, 400);
  }

  /* ----------------------------------------------------------
     3b. Portrait normalisation

     Five product shots on one shared canvas still do not read as one
     set: a squat jar and a tall bottle sit inside that canvas at
     completely different scales, so matching the files matches nothing
     the eye can see. This measures where the object actually is inside
     its file and scales THAT to a common visual area, which is what
     makes five objects read as five portraits of the same series.

     Measured once per image, from a downsampled copy — the bounds are
     a property of the file, so only the fit is recomputed on resize.
     Every failure path (a tainted canvas, an image that never decodes)
     leaves the plain contained image in place, which is the layout
     this replaces rather than depends on.
     ---------------------------------------------------------- */
  var fitCache = new WeakMap();

  /* The bounds of the object itself, as a fraction of the file.

     Two ways a shot can carry its background: transparency, or the
     paper it was shot on. Skin Moods shoots on #F7F6F2, so alpha alone
     finds nothing — the corner pixel is read as the background and
     every pixel is compared against it. Handles both. */
  function objectBounds(img) {
    if (fitCache.has(img)) return fitCache.get(img);
    var w = img.naturalWidth;
    var h = img.naturalHeight;
    if (!w || !h) return null;

    /* A sixth of a megapixel is far more than enough to find an edge,
       and keeps the whole set well under a frame. */
    var scale = Math.min(1, 200 / w, 200 / h);
    var cw = Math.max(1, Math.round(w * scale));
    var ch = Math.max(1, Math.round(h * scale));
    var canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;

    var data;
    try {
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, cw, ch);
      data = ctx.getImageData(0, 0, cw, ch).data;
    } catch (e) {
      return null; /* Cross-origin without CORS headers. */
    }

    /* The four corners, averaged — one stray corner cannot move it. */
    var corners = [0, (cw - 1) * 4, (ch - 1) * cw * 4, ((ch - 1) * cw + cw - 1) * 4];
    var br = 0, bg = 0, bb = 0;
    for (var c = 0; c < 4; c++) {
      br += data[corners[c]];
      bg += data[corners[c] + 1];
      bb += data[corners[c] + 2];
    }
    br /= 4; bg /= 4; bb /= 4;

    /* 14 of 255 clears sensor noise and the outer falloff of a soft
       shadow — which is background, not object — while still catching
       a pale bottle cap against pale paper. */
    var TOL = 14;
    var minX = cw, minY = ch, maxX = -1, maxY = -1;
    for (var y = 0; y < ch; y++) {
      for (var x = 0; x < cw; x++) {
        var i = (y * cw + x) * 4;
        var off = data[i + 3] < 24 ? 0 : Math.max(
          Math.abs(data[i] - br),
          Math.abs(data[i + 1] - bg),
          Math.abs(data[i + 2] - bb)
        );
        if (off > TOL) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    /* Nothing found, or the object already fills the file: either way
       there is nothing to normalise, and the plain contained image is
       already right. */
    if (maxX < 0) return null;

    var box = {
      x: minX / cw,
      y: minY / ch,
      w: (maxX - minX + 1) / cw,
      h: (maxY - minY + 1) / ch
    };
    fitCache.set(img, box);
    return box;
  }

  function fitPortrait(img) {
    var frame = img.parentNode;
    if (!frame) return;

    /* On the Science page's tablet rail the objects are a composition,
       not a portrait — three of them on one baseline, sized by height.
       Nothing to fit. The desktop plate and the phone sequence both show
       one object per collection inside an identical frame, and both need
       it measured.

       Named by its frame rather than by width alone: the exemption is
       that one rail, not the tablet in general. The Landing page's
       catalogue puts objects in a grid at every width and needs them
       normalised at all of them. */
    if (frame.classList.contains('sm-ed-five__set') &&
        window.matchMedia('(min-width: 768px) and (max-width: 899px)').matches) {
      img.style.transform = '';
      return;
    }

    var box = objectBounds(img);
    if (!box) return;

    var fw = img.clientWidth;
    var fh = img.clientHeight;
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!fw || !fh || !nw || !nh) return;

    /* Where `contain` actually drew the canvas inside the element. */
    var k = Math.min(fw / nw, fh / nh);
    var dw = nw * k;
    var dh = nh * k;
    var ox = (fw - dw) / 2;
    var oy = (fh - dh) / 2;

    /* The object's bounds in element coordinates: its own centre line,
       and its own foot — neither of which is the middle of the file. */
    var bw = box.w * dw;
    var bh = box.h * dh;
    var cx = ox + (box.x + box.w / 2) * dw;
    var foot = oy + (box.y + box.h) * dh;

    /* Equal area, not equal box. Fitting each object to the frame makes
       a wide jar span the whole column while a narrow bottle occupies a
       sliver of it — the frames match and nothing else does. Matching
       the area each object covers is what actually reads as one scale
       across five different shapes.

       Clamped so nothing can leave its frame: a tall object reaches the
       frame's height before it reaches the target area, and stops. */
    var target = 0.34 * fw * fh;
    var s = Math.min(
      Math.sqrt(target / (bw * bh)),
      fw / bw,
      fh / bh
    );

    /* Horizontally the object is centred on its own middle. Vertically
       it depends on whether the five frames share a line.

       On desktop they do — one row, one height — so each object's foot
       goes on its frame's floor and all five stand on one shelf, which
       centring could not do: the objects are different heights and
       their files carry different amounts of empty space below them.

       Down the phone sequence each collection is its own full-width
       plate with nothing beside it, so there is no shelf to stand on.
       A short jar dropped to the floor of its frame just opens a hole
       under the collection name, so there the object is centred in its
       own frame instead.

       Solved against a centre transform-origin: a point p maps to
       C + t + s(p - C), so t is whatever carries the measured point to
       where it should land. */
    var tx = -s * (cx - fw / 2);
    var ty;
    if (window.matchMedia('(max-width: 767px)').matches) {
      var mid = oy + (box.y + box.h / 2) * dh;
      ty = -s * (mid - fh / 2);
    } else {
      ty = (fh - s * (foot - fh / 2)) - fh / 2;
    }

    img.style.transformOrigin = 'center center';
    img.style.transform =
      'translate(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px) scale(' + s.toFixed(4) + ')';
  }

  function initPortraits(root) {
    var imgs = root.querySelectorAll('[data-sm-ed-fit]');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        if (img.complete && img.naturalWidth) fitPortrait(img);
        else img.addEventListener('load', function () { fitPortrait(img); }, { once: true });
      })(imgs[i]);
    }
  }

  function refitPortraits() {
    var imgs = document.querySelectorAll('[data-sm-ed-fit]');
    for (var i = 0; i < imgs.length; i++) fitPortrait(imgs[i]);
  }

  /* ----------------------------------------------------------
     3b. Lock-in
     A chapter holds still while its content advances. The marks
     are equal bands down the track; the observer's rootMargin
     collapses the viewport to a line across its middle, so the
     one band crossing that line is the active step. One
     observer per lock, no scroll listener, no rAF.

     Armed only where a held stage can show all of itself: a
     wide screen, a tall enough window, a fine pointer and no
     reduced-motion preference. Everywhere else the steps are
     left plainly stacked and the CSS never sticks anything.
     ---------------------------------------------------------- */
  var locks = [];

  function armLock(entry) {
    var wide = window.matchMedia('(min-width: 900px) and (min-height: 700px)').matches;
    var on = wide && canHover && !reduceMotion;

    entry.root.classList.toggle('is-armed', on);

    if (!on) {
      if (entry.observer) { entry.observer.disconnect(); entry.observer = null; }
      entry.current = -1;
      /* Leave every step lit — the section reads as a plain list. */
      for (var i = 0; i < entry.steps.length; i++) entry.steps[i].classList.add('is-on');
      return;
    }
    if (entry.observer) return;

    /* Arriving from the unarmed state — a resize past the breakpoint, or
       a first measurement taken while the window was too short — every
       step is lit from that branch. Clear them, or the chapter locks
       with its whole chain already built. The observer fires on observe,
       so the correct state lands on the next frame. */
    entry.current = -1;
    for (var c = 0; c < entry.steps.length; c++) entry.steps[c].classList.remove('is-on');

    entry.observer = new IntersectionObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        if (!records[r].isIntersecting) continue;
        var n = entry.marks.indexOf(records[r].target);
        if (n < 0 || n === entry.current) continue;
        entry.current = n;
        for (var s = 0; s < entry.steps.length; s++) {
          /* Everything up to the active step stays lit: the reader
             is meant to see the chain building, not one line at a
             time with the rest gone. */
          entry.steps[s].classList.toggle('is-on', s <= n);
        }
        entry.root.setAttribute('data-sm-ed-lock-on', String(n));
        entry.root.dispatchEvent(
          new CustomEvent('sm-ed:lock', { bubbles: true, detail: { step: n } })
        );
      }
    }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });

    for (var m = 0; m < entry.marks.length; m++) entry.observer.observe(entry.marks[m]);
  }

  function initLock(root) {
    if (root.getAttribute('data-sm-ed-ready') === 'true') return;
    root.setAttribute('data-sm-ed-ready', 'true');

    var steps = own(root, '[data-sm-ed-lock-step]', '[data-sm-ed-lock]');
    if (!steps.length) return;

    var track = root.querySelector('[data-sm-ed-lock-track]');
    var marksBox = root.querySelector('[data-sm-ed-lock-marks]');
    if (!track || !marksBox) return;

    /* The bands are written here rather than in Liquid: their count
       has to match the steps that actually rendered, and a section
       with an empty block would otherwise be one band out. */
    marksBox.textContent = '';
    var marks = [];
    for (var i = 0; i < steps.length; i++) {
      var band = document.createElement('span');
      marks.push(band);
      marksBox.appendChild(band);
    }
    root.style.setProperty('--sm-ed-lock-steps', String(steps.length));

    var entry = { root: root, steps: steps, marks: marks, observer: null, current: -1 };
    locks.push(entry);
    armLock(entry);
  }

  function rearmLocks() {
    for (var i = 0; i < locks.length; i++) armLock(locks[i]);
  }

  /* ----------------------------------------------------------
     3c. Hero film
     A muted, looping background video with a real control.

     Autoplay is started here rather than written as an attribute
     on the element. The attribute begins playing before any script
     can read the motion preference; started from script, a reader
     who asks for less motion keeps the poster — and keeps the
     button, so the choice is theirs rather than removed.

     The video is transparent until the `playing` event, so the
     still underneath is what shows while it loads, when autoplay
     is refused, and when there is nothing playable at all.
     ---------------------------------------------------------- */
  var heroFilms = [];

  function initHeroFilm(root) {
    if (root.getAttribute('data-sm-hero-ready') === 'true') return;
    var video = root.querySelector('[data-sm-hero-video]');
    if (!video) return;
    root.setAttribute('data-sm-hero-ready', 'true');

    var toggle = root.querySelector('[data-sm-hero-toggle]');
    var entry = { root: root, mq: null, onBreak: null };
    heroFilms.push(entry);

    function label() {
      if (!toggle) return;
      var playing = !video.paused && !video.ended;
      toggle.classList.toggle('is-playing', playing);
      var text = toggle.getAttribute(playing ? 'data-sm-hero-pause' : 'data-sm-hero-play');
      if (text) toggle.setAttribute('aria-label', text);
    }

    function start() {
      var attempt = video.play();
      /* Refused autoplay rejects rather than throwing. Nothing to
         recover: the poster is already on screen and the button is
         already the way in. */
      if (attempt && attempt.catch) attempt.catch(label);
    }

    video.addEventListener('playing', function () {
      root.classList.add('is-video-on');
      label();
    });
    video.addEventListener('pause', label);
    video.addEventListener('ended', label);

    /* A <source> that cannot load fires at the source element and does
       not bubble, so this is captured on the way down. Either way there
       is nothing to control, and a button that does nothing is worse
       than no button. */
    video.addEventListener('error', function () {
      root.classList.remove('is-video-on');
      if (toggle) toggle.hidden = true;
    }, true);

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused || video.ended) start();
        else video.pause();
        label();
      });
    }

    /* Two files, one element: the media attribute on a <source> is
       resolved once, when the resource is selected. Crossing the
       breakpoint — a rotated tablet, a dragged window — has to ask
       for the selection again or the wrong file keeps playing. */
    if (video.querySelectorAll('source[media]').length > 1) {
      entry.mq = window.matchMedia('(min-width: 768px)');
      entry.onBreak = function () {
        var wasPlaying = !video.paused && !video.ended;
        video.load();
        if (wasPlaying) start();
        label();
      };
      if (entry.mq.addEventListener) entry.mq.addEventListener('change', entry.onBreak);
      else if (entry.mq.addListener) entry.mq.addListener(entry.onBreak);
    }

    label();
    if (!reduceMotion) start();
  }

  function dropHeroFilms() {
    for (var i = heroFilms.length - 1; i >= 0; i--) {
      if (document.contains(heroFilms[i].root)) continue;
      var entry = heroFilms[i];
      if (entry.mq && entry.onBreak) {
        if (entry.mq.removeEventListener) entry.mq.removeEventListener('change', entry.onBreak);
        else if (entry.mq.removeListener) entry.mq.removeListener(entry.onBreak);
      }
      heroFilms.splice(i, 1);
    }
  }

  /* ----------------------------------------------------------
     4. Chapter snapping
     Desktop pointers only. Touch keeps plain scrolling — snapping
     fights momentum — and reduced motion opts out of being moved
     anywhere the reader did not move themselves.
     ---------------------------------------------------------- */
  function initSnap() {
    if (!document.querySelector('[data-sm-ed-snap]')) return;
    var wide = window.matchMedia('(min-width: 900px)').matches;
    document.documentElement.classList.toggle(
      'sm-ed-snap',
      wide && canHover && !reduceMotion
    );
  }

  /* ----------------------------------------------------------
     Boot
     ---------------------------------------------------------- */
  function init(scope) {
    var root = scope || document;
    measureHeader();
    var indexes = root.querySelectorAll('[data-sm-ed-index]');
    for (var i = 0; i < indexes.length; i++) initIndex(indexes[i]);
    var fives = root.querySelectorAll('[data-sm-ed-five]');
    for (var f = 0; f < fives.length; f++) initFive(fives[f]);
    var picks = root.querySelectorAll('[data-sm-ed-pick]');
    for (var p = 0; p < picks.length; p++) initPick(picks[p]);
    var moods = root.querySelectorAll('[data-sm-moods]');
    for (var m = 0; m < moods.length; m++) initMoods(moods[m]);
    var lockRoots = root.querySelectorAll('[data-sm-ed-lock]');
    for (var l = 0; l < lockRoots.length; l++) initLock(lockRoots[l]);
    var heroes = root.querySelectorAll('[data-sm-hero]');
    for (var h = 0; h < heroes.length; h++) initHeroFilm(heroes[h]);
    initReveal(root);
    initPortraits(root);
    initSnap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor re-renders a section in place. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* And throws the old markup away. The observers it left behind
     hold references to nodes that are no longer in the document,
     so a few edits would otherwise stack up dead locks that still
     get re-armed on every resize. */
  document.addEventListener('shopify:section:unload', function () {
    for (var i = locks.length - 1; i >= 0; i--) {
      if (document.contains(locks[i].root)) continue;
      if (locks[i].observer) locks[i].observer.disconnect();
      locks.splice(i, 1);
    }
    dropHeroFilms();
  });

  /* Once at the point the layout stops moving, and once more a beat
     later — the header is pulled out of flow by the theme's own script,
     which is not guaranteed to have run by `load`. */
  window.addEventListener('load', function () {
    measureHeader();
    sweepReveals();
    window.setTimeout(sweepReveals, 300);
  });
  var resizeTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      measureHeader();
      initSnap();
      rearmLocks();
      refitPortraits();
      sweepReveals();
    }, 150);
  });

  window.smEditorial = { init: init };
})();
